/**
 * What the outbox is holding, in the user's words, with the two ways out.
 *
 * The list follows the outbox live: a replay running in the background moves
 * a row from Waiting to Sending and takes it away when the server accepts it,
 * without the user having to close and reopen the sheet. Only a change the
 * server refused offers Retry and Discard — the rest are on their way and
 * nothing useful can be done to them.
 *
 * Rows a schema upgrade parked in `meta.orphanedOutbox` are shown too. They
 * can never replay (they were written against an older shape), so they are
 * read-only and there is one button to be rid of them.
 */

import React, { useCallback, useEffect, useState } from 'react';

import { Modal } from '@shared/components/ui';
import { useOfflineStatus } from '@shared/hooks/useOfflineStatus';
import {
  clearMeta,
  discard,
  labelForRow,
  listPending,
  readMeta,
  retry,
  subscribeToOutboxState,
} from '@shared/offline';

import type { PendingChange, WriteRequest } from '@shared/offline';

/** Meta key holding the rows a schema upgrade moved out of the outbox. */
const ORPHANED_KEY = 'orphanedOutbox';

/** The part of a parked outbox row this sheet can describe. */
interface OrphanedRow {
  id: string;
  method: WriteRequest['method'];
  path: string;
  body: unknown;
}

interface PendingChangesSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

/** "1 change" / "2 changes". */
const changes = (count: number): string => `${count} change${count === 1 ? '' : 's'}`;

/** Where a change stands, in words rather than a state name. */
function stateWord(change: PendingChange): string {
  if (change.state === 'inflight') return 'Sending';
  if (change.state === 'failed') return `Couldn't sync: ${change.lastError ?? 'unknown error'}`;
  return 'Waiting';
}

const ROW_CLASS = 'border-border flex items-start gap-3 border-b py-2 last:border-b-0';
const ACTION_CLASS =
  'bg-background hover:bg-muted text-foreground border-border rounded-md border px-2 py-0.5 text-xs transition-colors disabled:opacity-50';

interface PendingRowProps {
  change: PendingChange;
  online: boolean;
  onRetry: (id: string) => void;
  onDiscard: (id: string) => void;
}

/** One queued change: what it is, where it stands, and — if it failed — what to do. */
const PendingRow: React.FC<PendingRowProps> = ({ change, online, onRetry, onDiscard }) => (
  <li className={ROW_CLASS}>
    <div className="min-w-0 flex-1">
      <p className="text-foreground truncate text-sm">{change.label}</p>
      <p className="text-muted-foreground text-xs">{stateWord(change)}</p>
    </div>
    {change.state === 'failed' && (
      <div className="flex shrink-0 items-center gap-2">
        <button type="button" className={ACTION_CLASS} onClick={() => onRetry(change.id)}>
          Retry
        </button>
        <button
          type="button"
          className={ACTION_CLASS}
          disabled={!online}
          {...(online ? {} : { title: 'Connect to discard' })}
          onClick={() => onDiscard(change.id)}
        >
          Discard
        </button>
      </div>
    )}
  </li>
);

interface OrphanedSectionProps {
  rows: OrphanedRow[];
  onClear: () => void;
}

/** Changes an app update left behind: shown so they are not a silent loss. */
const OrphanedSection: React.FC<OrphanedSectionProps> = ({ rows, onClear }) => {
  if (rows.length === 0) return null;
  return (
    <section className="mt-4">
      <h3 className="text-muted-foreground text-xs font-medium">From a previous version</h3>
      <p className="text-muted-foreground mt-1 text-xs">
        These were saved by an older version of the app and can no longer be sent.
      </p>
      <ul className="mt-2">
        {rows.map(row => (
          <li key={row.id} className="text-foreground truncate py-1 text-sm">
            {labelForRow(row)}
          </li>
        ))}
      </ul>
      <button type="button" className={`${ACTION_CLASS} mt-2`} onClick={onClear}>
        Clear
      </button>
    </section>
  );
};

/** The live list behind every pending count in the shell. */
export const PendingChangesSheet: React.FC<PendingChangesSheetProps> = ({ isOpen, onClose }) => {
  const { online } = useOfflineStatus();
  const [changesList, setChangesList] = useState<PendingChange[]>([]);
  const [orphaned, setOrphaned] = useState<OrphanedRow[]>([]);
  const [notice, setNotice] = useState<string | null>(null);

  // Re-read on every outbox notification while open, so a replay running in
  // the background moves the rows and their controls without a reopen.
  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    const load = (): void => {
      void listPending().then(rows => !cancelled && setChangesList(rows));
      void readMeta<OrphanedRow[]>(ORPHANED_KEY).then(
        rows => !cancelled && setOrphaned(rows ?? [])
      );
    };
    load();
    const dispose = subscribeToOutboxState(load);
    return () => {
      cancelled = true;
      dispose();
      // A notice describes the discard that produced it, not the sheet, so it
      // ends with the visit: it must not greet the user again on the next one.
      setNotice(null);
    };
  }, [isOpen]);

  const onRetry = useCallback((id: string) => void retry(id), []);

  const onDiscard = useCallback(async (id: string) => {
    const result = await discard(id);
    if (result.refused === 'inflight') {
      setNotice('That change is being sent right now; try again in a moment.');
      return;
    }
    setNotice(
      result.removedDependants > 0
        ? `Also removed ${changes(result.removedDependants)} that depended on it.`
        : null
    );
  }, []);

  const onClearOrphaned = useCallback(async () => {
    await clearMeta(ORPHANED_KEY);
    setOrphaned([]);
  }, []);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Pending changes" size="sm">
      {changesList.length === 0 ? (
        <p className="text-muted-foreground text-sm">Nothing waiting.</p>
      ) : (
        <ul>
          {changesList.map(change => (
            <PendingRow
              key={change.id}
              change={change}
              online={online}
              onRetry={onRetry}
              onDiscard={id => void onDiscard(id)}
            />
          ))}
        </ul>
      )}
      {notice && <p className="text-muted-foreground mt-3 text-xs">{notice}</p>}
      <OrphanedSection rows={orphaned} onClear={() => void onClearOrphaned()} />
    </Modal>
  );
};
